import { useMemo } from 'react'

import { Heading, SkeletonBox } from '@circlefin/components'
import { useMoney } from '@circlefin/hooks'
import { currencyToToken } from '@shared/components/common'
import useTranslation from 'next-translate/useTranslation'

import type { Deposit, Payout } from '@shared/graphql'

export interface FxTransactionHeadingProps {
  /**
   * Transaction Title.
   */
  title: string
  /**
   * Display loading state.
   */
  loading: boolean
  /**
   * The transaction data.
   */
  transaction?: Payout | Deposit
  /**
   * Custom Style?
   */
  className?: string
}

export const FxTransactionHeading: React.FC<FxTransactionHeadingProps> = ({
  title,
  loading = false,
  transaction,
  className,
}) => {
  const { t } = useTranslation('payments')
  const { money } = useMoney()
  const isLoading = useMemo(
    () => loading || transaction == null,
    [loading, transaction],
  )

  return (
    <Heading className={className} variant="page/sm">
      <Heading.Eyebrow>
        <SkeletonBox className="my-1 h-4 w-36" loading={isLoading}>
          {title}
        </SkeletonBox>
      </Heading.Eyebrow>
      <Heading.Header>
        <SkeletonBox className="my-2 h-8 w-48" loading={isLoading}>
          {transaction?.amount &&
            money({
              number: transaction.amount.amount,
              variant: currencyToToken(transaction.amount.currency),
              options: {
                symbol: true,
                signDisplay: false,
              },
            })}
        </SkeletonBox>
      </Heading.Header>
      <Heading.Intro>
        <SkeletonBox className="my-1 h-4 w-58" loading={isLoading}>
          {t('transactionDetail.transactionId', {
            id: transaction?.id,
          })}
        </SkeletonBox>
      </Heading.Intro>
    </Heading>
  )
}
